<template>
  <ContextColorProvider context="wealth">
    <div class="portfolio">
      <HeroContainer>
        <template #navbar="{ isNavbarTransparent }">
          <NavBar :transparent="isNavbarTransparent">
            <template #left>
              <BackButton @click="handleGoBackClick" />
            </template>
            <template #right>
              <ShowMoneyToggleButton />
            </template>
          </NavBar>
        </template>
        <template #hero>
          <PortfolioHero
            :portfolio-hero="portfolioHero"
            :is-loading="isPortfolioHeroLoading"
            :has-error="hasPortfolioHeroError"
            @on-retry="fetchPortfolioHero"
          />
        </template>
        <template #content>
          <main class="portfolio__main">
            <Tabs
              data-testid="portfolio-tabs"
              :on-change="onChangeTab"
              :selected-index="currentTabIndex"
            >
              <div class="portfolio__main__tabs-wrapper">
                <TabList data-testid="tab-list">
                  <Tab>Performance</Tab>
                  <Tab>Alocação</Tab>
                  <Tab>Ordens {{ tabOpenOrdersCount }}</Tab>
                  <Tab>Sobre</Tab>
                </TabList>
              </div>
              <TabPanels>
                <TabPanel>
                  <Performance :portfolio-id="portfolioId" />
                </TabPanel>
                <TabPanel>
                  <LoaderIndicator
                    v-if="isPortfolioHeroLoading"
                    size="32"
                    with-container
                  />
                  <Allocation
                    v-else
                    :portfolio-id="portfolioId"
                    :has-positions="hasPositions"
                    :is-custom="isCustom"
                  />
                </TabPanel>
                <TabPanel>
                  <Orders :portfolio-id="portfolioId" />
                </TabPanel>
                <TabPanel>
                  <About
                    :has-error="hasPortfolioDistributionError"
                    :fetch-portfolio-details="fetchPortfolioDistribution"
                    :portfolio-id="portfolioId"
                    :is-custom="isCustom"
                    :is-fixed-income-portfolio-type="isFixedIncomePortfolioType"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </main>
        </template>
      </HeroContainer>
    </div>
  </ContextColorProvider>
</template>

<script setup lang="ts">
import { BackButton } from '@/modules/common/components/buttons';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';
import { HeroContainer } from '@/modules/common/components/Hero';
import { BffInvestments } from '@/modules/wallet-builder/src/services/bff-investments';
import { IPortfolioDistributionResponse } from '@/modules/wallet-builder/src/services/types/models/IBffInvestments';
import {
  getPortfolioHero,
  IPortfolioHero
} from '@/modules/wealth/services/portfolio';
import { PortfolioModule } from '@/store/modules/PortfolioModule';
import {
  PortfolioCategories,
  SuggestionPortfolioAllocationType
} from '@/types/models/Portfolio';
import {
  ContextColorProvider,
  LoaderIndicator,
  NavBar,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@warrenbrasil/nebraska-web';
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import {
  About,
  Allocation,
  Orders,
  Performance,
  PortfolioHero
} from './components';
import { WealthPortfolioTabsIndexes, WealthPortfolioTabs } from './types';

const route = useRoute();
const router = useRouter();

const bffInvestments = new BffInvestments();

const portfolioHero = ref<IPortfolioHero | null>(null);
const isPortfolioHeroLoading = ref(true);
const hasPortfolioHeroError = ref(true);

const isPortfolioDistributionLoading = ref(true);
const hasPortfolioDistributionError = ref(false);
const portfolioDistribution = ref<IPortfolioDistributionResponse | null>(null);

const reloadCause = ref('');
const currentTabIndex = ref(WealthPortfolioTabsIndexes.Performance);
const tabOpenOrders = ref(0);
const tabOpenOrdersCount = computed(() => {
  if (tabOpenOrders.value === 0) return '';

  return tabOpenOrders.value >= 9 ? '(+9)' : `(${tabOpenOrders.value})`;
});

const isFixedIncomePortfolioType = computed(() => {
  return (
    portfolioHero.value?.allocationType ===
    SuggestionPortfolioAllocationType.FIXED_INCOME
  );
});

const portfolioId = computed(() => {
  const { id } = route.params;

  return id;
});

const portfolioType = computed(() => {
  const { type } = route.params;

  return type;
});

const hasPositions = computed(() => {
  return portfolioHero.value?.hasPositions || false;
});

const isCustom = computed(() => {
  return portfolioType.value === PortfolioCategories.Custom;
});

async function fetchPortfolioDistribution() {
  isPortfolioDistributionLoading.value = true;
  hasPortfolioDistributionError.value = false;

  try {
    const portfolio = await bffInvestments.getPortfolioDistribution(
      portfolioId.value
    );
    portfolioDistribution.value = portfolio;
  } catch (error) {
    hasPortfolioDistributionError.value = true;
  } finally {
    isPortfolioDistributionLoading.value = false;
  }
}

async function fetchPortfolioHero() {
  isPortfolioHeroLoading.value = true;
  hasPortfolioHeroError.value = false;

  try {
    portfolioHero.value = await getPortfolioHero(portfolioId.value);
    tabOpenOrders.value = portfolioHero.value.openedOrders;
  } catch (e) {
    hasPortfolioHeroError.value = true;
  } finally {
    isPortfolioHeroLoading.value = false;
  }
}

async function fetchPortfolioDetails(reloadCauseParam?: string) {
  reloadCauseParam && (reloadCause.value = reloadCauseParam);
  await Promise.all([fetchPortfolioDistribution(), fetchPortfolioHero()]);
}

async function validatePortfolio() {
  try {
    if (
      !PortfolioModule.getPortfolioById(portfolioId.value) ||
      !PortfolioModule.portfolios.length
    ) {
      await PortfolioModule.fetchPortfolios();
    }

    const portfolio = PortfolioModule.getPortfolioById(portfolioId.value);

    if (!portfolio) {
      router.push({ name: 'wealth' });
      return;
    }

    if (!portfolioType.value || portfolioType.value !== portfolio.type) {
      router.replace({
        name: 'wealthPortfolio',
        params: { type: portfolio!.type, id: portfolioId.value }
      });
    }
  } catch (error) {
    router.push({ name: 'wealth' });
  }
}

function onChangeTab(selectedTabIndex: WealthPortfolioTabsIndexes) {
  currentTabIndex.value = selectedTabIndex;
}

function handleGoBackClick() {
  router.push({ name: 'wealth' });
}

onMounted(async () => {
  const tabParamsMapper: Record<
    WealthPortfolioTabs,
    WealthPortfolioTabsIndexes
  > = {
    [WealthPortfolioTabs.Performance]: WealthPortfolioTabsIndexes.Performance,
    [WealthPortfolioTabs.Allocation]: WealthPortfolioTabsIndexes.Allocation,
    [WealthPortfolioTabs.Orders]: WealthPortfolioTabsIndexes.Orders,
    [WealthPortfolioTabs.About]: WealthPortfolioTabsIndexes.About
  };

  const tab = route.query?.tab as WealthPortfolioTabs;

  currentTabIndex.value =
    tabParamsMapper[tab] || WealthPortfolioTabsIndexes.Performance;

  await validatePortfolio();

  await fetchPortfolioDetails();
});

provide('portfolioDistribution', portfolioDistribution);
provide('isPortfolioDistributionLoading', isPortfolioDistributionLoading);
provide('reloadCause', reloadCause);
provide('currentTabIndex', currentTabIndex);
</script>

<style lang="less" scoped>
@page-width: 1008px;
@page-padding: calc(2 * @size-spacing-x400);

.portfolio {
  &__header {
    max-width: @page-width;
    margin: 0 auto;
  }

  &__main {
    &__tabs-wrapper {
      position: relative;
      border-bottom: @size-border-x400 solid @divider-primary;
    }

    .tabs-container {
      position: relative;
      top: @size-border-x400;
      max-width: calc(@page-width + @page-padding);
      width: 100%;
      margin: 0 auto;
      padding: 0 @size-spacing-x400;
    }
  }

  &__tabs-error {
    padding: @size-spacing-x1000 0;
    margin: @size-spacing-x600 auto @size-spacing-x600 auto;
  }
}

.hero-container {
  position: relative;

  &__wrapper {
    padding: @size-spacing-x1200 @size-spacing-x400;
  }

  &__content {
    max-width: 1366px;
    margin: 0 auto;
  }

  &__back {
    margin-bottom: @size-spacing-x600;
  }
}
</style>
